// src/pages/distribuicao/VendasTransportesGestaoPontos.js
import React, { useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import PageHeader from "pages/components/PageHeader";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import {
  Card,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";

import Paper from "@mui/material/Paper";
import Draggable from "react-draggable";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import PaidIcon from "@mui/icons-material/Paid";
import HistoryIcon from "@mui/icons-material/History";

function PaperComponent(props) {
  return (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

function fmtPts(v) {
  const n = Number(v || 0);
  return n.toFixed(2).replace(/\.?0+$/, "");
}

function mesAtual() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function mesMenos(n) {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function VendasTransportesGestaoPontos() {
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const isDistribuicao = temFuncao("Distribuição");
  const distribuicaoNome = String(userData?.distribuicaonome || "").trim();

  const podeVerPagina = isAdminLike || (isDistribuicao && !!distribuicaoNome);

  const [loading, setLoading] = useState(false);

  const [mesIni, setMesIni] = useState(() => mesMenos(5));
  const [mesFim, setMesFim] = useState(() => mesAtual());

  const [meses, setMeses] = useState([]);
  const [pessoas, setPessoas] = useState([]);

  const [dlgAjusteOpen, setDlgAjusteOpen] = useState(false);
  const [dlgAjusteSaving, setDlgAjusteSaving] = useState(false);
  const [ajusteRow, setAjusteRow] = useState(null);
  const [ajusteMes, setAjusteMes] = useState(null);
  const [ajustePontos, setAjustePontos] = useState("");
  const [ajusteJustificacao, setAjusteJustificacao] = useState("");

  const [dlgPagamentoOpen, setDlgPagamentoOpen] = useState(false);
  const [dlgPagamentoSaving, setDlgPagamentoSaving] = useState(false);
  const [pagamentoRow, setPagamentoRow] = useState(null);
  const [pagamentoPontos, setPagamentoPontos] = useState("");
  const [pagamentoJustificacao, setPagamentoJustificacao] = useState("");

  const [dlgHistOpen, setDlgHistOpen] = useState(false);
  const [dlgHistLoading, setDlgHistLoading] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [historicoPessoa, setHistoricoPessoa] = useState("");

  const atualizar = async (fazerSync = true) => {
    setLoading(true);

    if (fazerSync) {
      const [_, syncErr] = await handleApi(
        api.post("Distribuicao/SyncVendasTransportesGestaoPontos", {
          idutilizador: userData?.id || 0,
          mesIni,
          mesFim,
        })
      );

      if (syncErr) {
        showAlert(syncErr, "error");
        setLoading(false);
        return;
      }
    }

    const [dados, err, meta] = await handleApi(
      api.post("Distribuicao/SelVendasTransportesGestaoPontos", {
        idutilizador: userData?.id || 0,
        mesIni,
        mesFim,
      })
    );

    if (err) {
      showAlert(err, "error");
      setLoading(false);
      return;
    }

    setMeses(Array.isArray(dados?.meses) ? dados.meses : []);
    setPessoas(Array.isArray(dados?.pessoas) ? dados.pessoas : []);

    showAlert(meta?.message || "OK", meta?.severity || "success");
    setLoading(false);
  };

  const atualizarFuncionario = async (idfuncionario) => {
    if (!idfuncionario) return;

    const [dados, err] = await handleApi(
      api.post("Distribuicao/SelVendasTransportesGestaoPontosFuncionario", {
        idutilizador: userData?.id || 0,
        idfuncionario,
        mesIni,
        mesFim,
      })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    if (!dados?.idutilizador) return;

    setPessoas((prev) =>
      prev.map((p) => (Number(p.idutilizador) === Number(dados.idutilizador) ? dados : p))
    );
  };

  const abrirAjuste = (row, mes) => {
    if (!isAdminLike) return;

    if (!mes || mes.fechado) return;

    setAjusteRow(row);
    setAjusteMes(mes);
    setAjustePontos("");
    setAjusteJustificacao("");
    setDlgAjusteOpen(true);
  };

  const gravarAjuste = async () => {
    const pontos = Number(String(ajustePontos || "").replace(",", "."));

    if (!Number.isFinite(pontos) || pontos === 0) {
      showAlert("Indica os pontos a acrescentar ou retirar.", "warning");
      return;
    }

    if (!String(ajusteJustificacao || "").trim()) {
      showAlert("A justificação é obrigatória.", "warning");
      return;
    }

    setDlgAjusteSaving(true);

    const [_, err, meta] = await handleApi(
      api.post("Distribuicao/InsVendasTransportesGestaoPontosAjuste", {
        idutilizador: userData?.id || 0,
        idmensal: ajusteMes?.id || 0,
        pontos,
        justificacao: ajusteJustificacao,
      })
    );

    if (err) {
      showAlert(err, "error");
      setDlgAjusteSaving(false);
      return;
    }

    showAlert(meta?.message || "Ajuste registado.", meta?.severity || "success");
    setDlgAjusteSaving(false);
    setDlgAjusteOpen(false);

    await atualizarFuncionario(ajusteRow?.idutilizador);
  };

  const abrirPagamento = (row) => {
    setPagamentoRow(row);
    setPagamentoPontos("");
    setPagamentoJustificacao("");
    setDlgPagamentoOpen(true);
  };

  const gravarPagamento = async () => {
    const pontos = Number(String(pagamentoPontos || "").replace(",", "."));

    if (!Number.isFinite(pontos) || pontos <= 0) {
      showAlert("Indica os pontos pagos.", "warning");
      return;
    }

    setDlgPagamentoSaving(true);

    const [_, err, meta] = await handleApi(
      api.post("Distribuicao/InsVendasTransportesGestaoPontosPagamento", {
        idutilizador: userData?.id || 0,
        idfuncionario: pagamentoRow?.idutilizador || 0,
        pontos,
        justificacao: pagamentoJustificacao || "Pagamento de pontos.",
      })
    );

    if (err) {
      showAlert(err, "error");
      setDlgPagamentoSaving(false);
      return;
    }

    showAlert(meta?.message || "Pagamento registado.", meta?.severity || "success");
    setDlgPagamentoSaving(false);
    setDlgPagamentoOpen(false);
    await atualizarFuncionario(pagamentoRow?.idutilizador);
  };

  const abrirHistorico = async (row = null) => {
    setDlgHistOpen(true);
    setDlgHistLoading(true);
    setHistorico([]);
    setHistoricoPessoa(String(row?.pessoa || "").trim());

    const [dados, err, meta] = await handleApi(
      api.post("Distribuicao/SelVendasTransportesGestaoPontosHistorico", {
        idutilizador: userData?.id || 0,
        mesIni,
        mesFim,
      })
    );

    if (err) {
      showAlert(err, "error");
      setDlgHistLoading(false);
      return;
    }

    const lista = Array.isArray(dados) ? dados : [];
    setHistorico(
      row?.idutilizador ? lista.filter((h) => h.idutilizador === row.idutilizador) : lista
    );
    showAlert(meta?.message || "Histórico carregado.", meta?.severity || "success");
    setDlgHistLoading(false);
  };

  const totalPendentePessoa = (row) =>
    (row?.meses || []).reduce((acc, m) => acc + Number(m?.pontos_pendentes || 0), 0);

  const obterMes = (row, mesRef) =>
    (row?.meses || []).find(
      (m) => Number(m.ano) === Number(mesRef.ano) && Number(m.mes) === Number(mesRef.mes)
    );

  const tableRows = useMemo(
    () =>
      (pessoas || []).map((row) => {
        const mesesMap = {};
        (row.meses || []).forEach((m) => {
          mesesMap[`${m.ano}-${m.mes}`] = m;
        });

        return {
          ...row,
          mesesMap,
          pendente_total: totalPendentePessoa(row),
        };
      }),
    [pessoas]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Funcionário",
        accessor: "pessoa",
        sortType: "textPt",
        width: 260,
        minWidth: 180,
      },
      ...meses.map((mRef) => ({
        Header: mRef.label,
        id: `mes_${mRef.ano}_${mRef.mes}`,
        accessor: (row) => Number(row.mesesMap?.[`${mRef.ano}-${mRef.mes}`]?.pontos_pendentes || 0),
        sortType: "numPt",
        width: 135,
        minWidth: 120,
        Cell: ({ row }) => {
          const m = row.original.mesesMap?.[`${mRef.ano}-${mRef.mes}`];

          if (!m) return "—";

          const pago = Number(m.pontos_pagos || 0);
          const pendente = Number(m.pontos_pendentes || 0);
          const total = Number(m.pontos_total || 0);
          const fechado = !!m.fechado;

          return (
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              sx={{
                opacity: fechado ? 0.65 : 1,
              }}
            >
              {pago > 0 && (
                <span style={{ color: "#888", fontSize: 11 }}>Pago: {fmtPts(pago)}</span>
              )}

              <span
                style={{
                  fontWeight: 800,
                  color: pendente > 0 ? "#1a237e" : "#555",
                }}
              >
                {fmtPts(pendente)}
              </span>

              <span style={{ fontSize: 10, opacity: 0.65 }}>Total: {fmtPts(total)}</span>

              {isAdminLike && (
                <Tooltip title={fechado ? "Mês fechado" : "Registar ajuste"}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => abrirAjuste(row.original, m)}
                      disabled={fechado}
                      style={{ padding: 2 }}
                    >
                      <EditIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </MDBox>
          );
        },
      })),
      {
        Header: "Pendente",
        accessor: "pendente_total",
        sortType: "numPt",
        width: 120,
        minWidth: 110,
        Cell: ({ value }) => (
          <span style={{ fontWeight: 800, color: "#1a237e" }}>{fmtPts(value)}</span>
        ),
      },
    ],
    [meses]
  );

  const rowActions = useMemo(() => {
    const actions = [
      {
        id: "historico",
        tooltip: "Consultar histórico",
        icon: <HistoryIcon fontSize="small" />,
        color: "info",
        onClick: abrirHistorico,
      },
    ];

    if (isAdminLike) {
      actions.unshift({
        id: "pagar",
        tooltip: "Registar pontos pagos",
        icon: <PaidIcon fontSize="small" />,
        color: "success",
        visible: (row) => Number(row.pendente_total || 0) > 0,
        onClick: abrirPagamento,
      });
    }

    return actions;
  }, [isAdminLike]);

  const historicoColumns = useMemo(
    () => [
      {
        Header: "Data",
        accessor: "criado_em",
        sortType: "textPt",
        width: 160,
        Cell: ({ value }) =>
          String(value || "")
            .replace("T", " ")
            .slice(0, 19),
      },
      { Header: "Mês", accessor: "mes_label", sortType: "textPt", width: 90 },
      { Header: "Tipo", accessor: "tipo", sortType: "textPt", width: 120 },
      {
        Header: "Pontos",
        accessor: "pontos",
        sortType: "numPt",
        width: 100,
        Cell: ({ value }) => <span style={{ fontWeight: 700 }}>{fmtPts(value)}</span>,
      },
      { Header: "Justificação", accessor: "justificacao", sortType: "textPt", width: 420 },
      { Header: "Utilizador", accessor: "criado_por_nome", sortType: "textPt", width: 220 },
    ],
    []
  );

  if (!podeVerPagina) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <PageHeader />
        <MDBox pt={2} pb={3}>
          <Card>
            <MDBox p={2}>
              <MDTypography style={{ fontSize: 14, fontWeight: 700 }}>
                Sem permissões para consultar a Gestão de Pontos.
              </MDTypography>
            </MDBox>
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <PageHeader />

      <MDBox pt={2} pb={3}>
        <Card>
          <MDBox p={2}>
            <MDTypography style={{ fontSize: 12, opacity: 0.85 }}>
              Indica o período em meses. Os pontos base são recalculados enquanto o mês não estiver
              fechado.
            </MDTypography>

            <MDBox mt={2} display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
              <div style={{ minWidth: 160 }}>
                <div style={{ fontSize: 12, marginBottom: 6 }}>Mês inicial</div>
                <TextField
                  type="month"
                  value={mesIni}
                  onChange={(e) => setMesIni(e.target.value)}
                  size="small"
                  fullWidth
                />
              </div>

              <div style={{ minWidth: 160 }}>
                <div style={{ fontSize: 12, marginBottom: 6 }}>Mês final</div>
                <TextField
                  type="month"
                  value={mesFim}
                  onChange={(e) => setMesFim(e.target.value)}
                  size="small"
                  fullWidth
                />
              </div>

              <MDButton color="info" onClick={atualizar}>
                Atualizar
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>

        <MDBox mt={2}>
          <ProjectTable
            tableKey="vendas-transportes-gestao-pontos"
            version="1"
            title="Pontos por funcionário"
            titleNote="Pontos de cada mês serão pagos 3 meses depois. Ex. Os pontos de Julho serão pagos até ao dia 10 de Outubro."
            subtitle="Valores mensais pendentes, pagos e totais"
            columns={columns}
            data={tableRows}
            loading={loading}
            emptyMessage="Sem dados. Clica em Atualizar."
            minWidth={Math.max(900, 420 + meses.length * 135)}
            maxHeight="68vh"
            rowActions={rowActions}
            actionsPosition="left"
            defaultSortBy={[{ id: "pessoa", desc: false }]}
            showKeepFilters
          />
        </MDBox>
      </MDBox>

      <Dialog
        open={dlgAjusteOpen}
        onClose={() => setDlgAjusteOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperComponent={PaperComponent}
      >
        <DialogTitle id="draggable-dialog-title" sx={dialogTitleStyle}>
          Ajuste — {ajusteRow?.pessoa} — {ajusteMes?.label}
          <IconButton size="small" onClick={() => setDlgAjusteOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Pontos a acrescentar/retirar"
              size="small"
              value={ajustePontos}
              onChange={(e) => setAjustePontos(e.target.value)}
              helperText="Usa valor positivo para acrescentar, negativo para retirar."
              fullWidth
            />

            <TextField
              label="Justificação"
              size="small"
              value={ajusteJustificacao}
              onChange={(e) => setAjusteJustificacao(e.target.value)}
              multiline
              minRows={3}
              required
              fullWidth
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={() => setDlgAjusteOpen(false)}>
            Cancelar
          </MDButton>
          <MDButton color="info" onClick={gravarAjuste} disabled={dlgAjusteSaving}>
            {dlgAjusteSaving ? "A gravar..." : "Gravar"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dlgPagamentoOpen}
        onClose={() => setDlgPagamentoOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperComponent={PaperComponent}
      >
        <DialogTitle id="draggable-dialog-title" sx={dialogTitleStyle}>
          Pagamento — {pagamentoRow?.pessoa}
          <IconButton size="small" onClick={() => setDlgPagamentoOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2}>
            <MDTypography style={{ fontSize: 12, opacity: 0.85 }}>
              O pagamento será abatido automaticamente aos meses pendentes mais antigos.
            </MDTypography>

            <TextField
              label="Pontos pagos"
              size="small"
              value={pagamentoPontos}
              onChange={(e) => setPagamentoPontos(e.target.value)}
              fullWidth
            />

            <TextField
              label="Justificação / observação"
              size="small"
              value={pagamentoJustificacao}
              onChange={(e) => setPagamentoJustificacao(e.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={() => setDlgPagamentoOpen(false)}>
            Cancelar
          </MDButton>
          <MDButton color="info" onClick={gravarPagamento} disabled={dlgPagamentoSaving}>
            {dlgPagamentoSaving ? "A gravar..." : "Gravar"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dlgHistOpen}
        onClose={() => setDlgHistOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperComponent={PaperComponent}
      >
        <DialogTitle id="draggable-dialog-title" sx={dialogTitleStyle}>
          Histórico{historicoPessoa ? ` — ${historicoPessoa}` : ""}
          <IconButton size="small" onClick={() => setDlgHistOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <ProjectTable
            tableKey="vendas-transportes-gestao-pontos-historico"
            version="1"
            title={`${mesIni} a ${mesFim}`}
            subtitle="Movimentos de apuramento, recálculo, ajustes, pagamentos e fechos"
            columns={historicoColumns}
            data={historico}
            loading={dlgHistLoading}
            emptyMessage="Sem histórico no período indicado."
            minWidth={1050}
            maxHeight="50vh"
            defaultSortBy={[{ id: "criado_em", desc: true }]}
            showKeepFilters
          />
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={() => setDlgHistOpen(false)}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

const dialogTitleStyle = {
  cursor: "move",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};
